import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import { usePageTitles } from "@/contexts/PageTitlesContext";

const PAGES = [
  { key: "home", label_zh: "首页 Hero", label_en: "Home Hero" },
  { key: "about", label_zh: "晶灵简介", label_en: "About" },
  { key: "events", label_zh: "零度空间", label_en: "Zero Space" },
  { key: "products", label_zh: "晶灵产品", label_en: "Products" },
  { key: "angelina", label_zh: "Angelina", label_en: "Angelina" },
  { key: "crystals", label_zh: "水晶介绍", label_en: "Crystals" },
  { key: "booking", label_zh: "人生预约", label_en: "Life Booking" },
  { key: "dashboard", label_zh: "会员系统", label_en: "Member Dashboard" },
];

export default function AdminPageTitles() {
  const { t } = useTranslation();
  const { refresh } = usePageTitles();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/page-titles").then((r) => {
      const stripped = { ...r.data };
      delete stripped.id;
      delete stripped.updated_at;
      setForm(stripped);
    });
  }, []);

  const patchField = (page, field, value) => {
    setForm((f) => ({ ...f, [page]: { ...(f[page] || {}), [field]: value } }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/admin/page-titles", form);
      await refresh();
      toast.success(t("common.saved"));
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const inputCls =
    "w-full px-3 py-2 rounded-md border border-[#e5ebeb] focus:border-[#00868e] outline-none bg-white text-sm transition-colors duration-200";

  return (
    <form onSubmit={submit} className="p-6 md:p-10 max-w-5xl" data-testid="admin-page-titles">
      <h1 className="font-serif-cn text-3xl text-[#003645] font-semibold">页面标题 · Page Titles</h1>
      <p className="mt-2 text-sm text-[#4a6e77]">编辑每个主要页面的标题与副标题（中英双语）。留空即使用默认。</p>

      <div className="mt-8 space-y-5">
        {PAGES.map((p) => {
          const v = form[p.key] || {};
          return (
            <div key={p.key} className="rounded-lg border border-[#e5ebeb] bg-white p-5" data-testid={`pt-block-${p.key}`}>
              <div className="text-xs uppercase tracking-widest text-[#00868e] font-semibold">
                {p.label_zh} · {p.label_en}
              </div>
              <div className="mt-3 grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] text-[#4a6e77]">标题 (中)</label>
                  <input className={inputCls} value={v.title_zh || ""} onChange={(e) => patchField(p.key, "title_zh", e.target.value)} data-testid={`pt-${p.key}-title-zh`} />
                </div>
                <div>
                  <label className="text-[11px] text-[#4a6e77]">Title (EN)</label>
                  <input className={inputCls} value={v.title_en || ""} onChange={(e) => patchField(p.key, "title_en", e.target.value)} data-testid={`pt-${p.key}-title-en`} />
                </div>
                <div>
                  <label className="text-[11px] text-[#4a6e77]">副标题 (中)</label>
                  <input className={inputCls} value={v.subtitle_zh || ""} onChange={(e) => patchField(p.key, "subtitle_zh", e.target.value)} data-testid={`pt-${p.key}-subtitle-zh`} />
                </div>
                <div>
                  <label className="text-[11px] text-[#4a6e77]">Subtitle (EN)</label>
                  <input className={inputCls} value={v.subtitle_en || ""} onChange={(e) => patchField(p.key, "subtitle_en", e.target.value)} data-testid={`pt-${p.key}-subtitle-en`} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <button type="submit" disabled={saving} className="mt-8 inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#005870] text-white font-medium hover:bg-[#003645] disabled:opacity-60" data-testid="page-titles-save-btn">
        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
        {t("common.save")}
      </button>
    </form>
  );
}
